import React, { useState, useEffect } from 'react';
import { Box, Avatar, Typography, IconButton, Fade, Slide } from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { styled, alpha } from '@mui/material/styles';

const PopupContainer = styled(Box)(({ theme, bottomOffset = 20 }) => ({
  position: 'fixed',
  bottom: `${bottomOffset}px`,
  left: '50%',
  transform: 'translateX(-50%)',
  zIndex: 9999,
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  maxWidth: '320px',
  width: '90%',
  pointerEvents: 'none',

  '& > *': {
    pointerEvents: 'auto',
  },

  '@media (max-width: 600px)': {
    maxWidth: 'calc(100vw - 40px)',
    bottom: `${Math.max(80, bottomOffset + 60)}px`,
  },
}));

const NotificationCard = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  padding: '10px 14px',
  background: theme.palette.mode === 'dark'
    ? `linear-gradient(135deg, ${alpha('#1a1f2e', 0.98)}, ${alpha('#141820', 0.95)})`
    : `linear-gradient(135deg, ${alpha('#ffffff', 0.98)}, ${alpha('#f8f9fa', 0.95)})`,
  backdropFilter: 'blur(20px)',
  borderRadius: '24px',
  boxShadow: theme.palette.mode === 'dark'
    ? `0 4px 16px ${alpha('#000', 0.4)}, 0 0 0 1px ${alpha('#fff', 0.08)}`
    : `0 4px 16px ${alpha('#000', 0.12)}, 0 0 0 1px ${alpha('#000', 0.06)}`,
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  border: `1px solid ${theme.palette.mode === 'dark' ? alpha('#fff', 0.1) : alpha('#000', 0.08)}`,

  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: theme.palette.mode === 'dark'
      ? `0 8px 24px ${alpha('#000', 0.5)}, 0 0 0 1px ${alpha('#fff', 0.12)}`
      : `0 8px 24px ${alpha('#000', 0.16)}, 0 0 0 1px ${alpha('#000', 0.1)}`,
  },
}));

const NotificationContent = styled(Box)({
  flex: 1,
  minWidth: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: '2px',
});

const NotificationTitle = styled(Typography)(({ theme }) => ({
  fontSize: '13px',
  fontWeight: 600,
  color: theme.palette.mode === 'dark' ? '#fff' : '#000',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}));

const MessageCount = styled(Box)(({ theme }) => ({
  minWidth: '20px',
  height: '20px',
  borderRadius: '10px',
  background: '#25D366',
  color: '#000',
  fontSize: '12px',
  fontWeight: 700,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '0 6px',
}));

const NotificationTime = styled(Typography)(({ theme }) => ({
  display: 'none',
}));

const CloseButton = styled(IconButton)(({ theme }) => ({
  padding: '2px',
  color: theme.palette.mode === 'dark' ? alpha('#fff', 0.6) : alpha('#000', 0.6),
  transition: 'all 0.2s ease',

  '&:hover': {
    color: theme.palette.error.main,
    background: alpha(theme.palette.error.main, 0.1),
  },

  '& .MuiSvgIcon-root': {
    fontSize: '16px',
  },
}));

const NotificationPopup = ({ notifications, onDismiss, onNotificationClick, bottomOffset = 20 }) => {
  const [visibleNotifications, setVisibleNotifications] = useState([]);

  useEffect(() => {
    // Group notifications by sender - combine multiple from same sender into one with count
    if (notifications.length > 0) {
      const grouped = [];
      const senderCounts = {};

      notifications.forEach((notif) => {
        const senderKey = notif.senderName || notif.roomId;
        if (!senderCounts[senderKey]) {
          senderCounts[senderKey] = { count: 0, data: notif };
        }
        senderCounts[senderKey].count += 1;
      });

      // Convert to array, max 3 groups
      Object.values(senderCounts).slice(0, 3).forEach((item) => {
        grouped.push({
          ...item.data,
          messageCount: item.count,
        });
      });

      setVisibleNotifications(grouped);

      // Auto-dismiss after 5 seconds per group
      const timers = grouped.map((notification, index) => {
        return setTimeout(() => {
          // Dismiss all notifications from this sender
          notifications
            .filter(n => (n.senderName || n.roomId) === (notification.senderName || notification.roomId))
            .forEach(n => onDismiss(n.id));
        }, 5000 + (index * 200));
      });

      return () => {
        timers.forEach(timer => clearTimeout(timer));
      };
    } else {
      setVisibleNotifications([]);
    }
  }, [notifications, onDismiss]);

  const handleNotificationClick = (notification) => {
    if (onNotificationClick) {
      onNotificationClick(notification);
    }
    // Dismiss ALL notifications from this sender
    const senderKey = notification.senderName || notification.roomId;
    notifications
      .filter(n => (n.senderName || n.roomId) === senderKey)
      .forEach(n => onDismiss(n.id));
  };

  const formatTime = (timestamp) => {
    const now = new Date();
    const time = new Date(timestamp);
    const diff = Math.floor((now - time) / 1000); // seconds

    if (diff < 60) return 'Just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return time.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  };

  return (
    <PopupContainer bottomOffset={bottomOffset}>
      {visibleNotifications.map((notification, index) => (
        <Slide
          key={notification.id}
          direction="up"
          in
          timeout={300}
          style={{ transitionDelay: `${index * 100}ms` }}
        >
          <Fade in timeout={400}>
            <NotificationCard onClick={() => handleNotificationClick(notification)}>
              <Avatar
                src={notification.avatar}
                alt={notification.senderName}
                sx={{ width: 36, height: 36 }}
              />
              <NotificationContent>
                <NotificationTitle>{notification.senderName}</NotificationTitle>
              </NotificationContent>
              {notification.messageCount > 1 && (
                <MessageCount>{notification.messageCount}</MessageCount>
              )}
              <CloseButton
                onClick={(e) => {
                  e.stopPropagation();
                  // Dismiss all from this sender
                  notifications
                    .filter(n => (n.senderName || n.roomId) === (notification.senderName || notification.roomId))
                    .forEach(n => onDismiss(n.id));
                }}
                aria-label="Dismiss notification"
              >
                <CloseIcon />
              </CloseButton>
            </NotificationCard>
          </Fade>
        </Slide>
      ))}
    </PopupContainer>
  );
};

export default NotificationPopup;
